import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { GetMinimumTotalAmountDataModel } from "features/stock-ordering/core/domain/get-minimum-total-amount.model";
import {
  GetMinimumTotalAmountDataRepository,
  GetMinimumTotalAmountDataResponse,
} from "features/stock-ordering/data/stock-ordering.repository";

export enum GetMinimumTotalAmountDataState {
  initial,
  inProgress,
  success,
  fail,
}

interface InitialState {
  status: GetMinimumTotalAmountDataState;
  message: string;
  data: GetMinimumTotalAmountDataModel | undefined;
}

const initialState: InitialState = {
  status: GetMinimumTotalAmountDataState.initial,
  message: "",
  data: undefined,
};

export const getMinimumTotalAmountData = createAsyncThunk(
  "getMinimumTotalAmountData",
  async (_, { rejectWithValue }) => {
    try {
      const response: GetMinimumTotalAmountDataResponse =
        await GetMinimumTotalAmountDataRepository();
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

/* Main Slice */
export const getMinimumTotalAmountDataSlice = createSlice({
  name: "getMinimumTotalAmountData",
  initialState,
  reducers: {
    resetGetMinimumTotalAmountData: (state) => {
      state.status = GetMinimumTotalAmountDataState.inProgress;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMinimumTotalAmountData.pending, (state) => {
        state.status = GetMinimumTotalAmountDataState.inProgress;
      })
      .addCase(getMinimumTotalAmountData.fulfilled, (state, action) => {
        if (action.payload) {
          const { message, data } = action.payload;
          state.status = GetMinimumTotalAmountDataState.success;
          state.message = message;
          state.data = data;
        }
      })
      .addCase(getMinimumTotalAmountData.rejected, (state, action) => {
        state.status = GetMinimumTotalAmountDataState.fail;
        state.message = action.payload as string;
        state.data = undefined;
      });
  },
});

export const selectGetMinimumTotalAmountData = (state: RootState) =>
  state.getMinimumTotalAmountData;

export const { resetGetMinimumTotalAmountData } =
  getMinimumTotalAmountDataSlice.actions;
export default getMinimumTotalAmountDataSlice.reducer;
